import { produce } from 'immer'
import {
  SET_RECEIPT_ATTACHMENT_IDS,
  SetReceiptAttachmentIdsAction,
} from 'src/app/UpdateVirtualCardForm/redux/updateVirtualCardForm.actions'
import { isHandledPaywall402 } from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import {
  updateBillPayRouteProps,
  updateVirtualCardRouteProps,
} from 'src/navigation/routeProps'
import {
  GET_CREDIT_CARDS_FAILURE,
  GET_CREDIT_CARDS_INIT,
  GET_CREDIT_CARDS_SUCCESS,
} from 'src/redux/actions/data/creditCards.actions'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions,
} from 'src/redux/actions/data/receiptAttachments.actions'
import {
  PUT_VIRTUAL_CARD_CANCEL_FAILURE,
  PUT_VIRTUAL_CARD_CANCEL_INIT,
  PUT_VIRTUAL_CARD_CANCEL_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS,
  PUT_VIRTUAL_CARD_FAILURE,
  PUT_VIRTUAL_CARD_INIT,
  PUT_VIRTUAL_CARD_REJECT_FAILURE,
  PUT_VIRTUAL_CARD_REJECT_INIT,
  PUT_VIRTUAL_CARD_REJECT_SUCCESS,
  PUT_VIRTUAL_CARD_SUCCESS,
  PutVirtualCardActions,
  PutVirtualCardCancelActions,
  PutVirtualCardCancelUpdateActions,
  PutVirtualCardRejectActions,
  SET_VIRTUAL_CARD_FAILURE,
  SetVirtualCardFailureAction,
} from 'src/redux/actions/data/virtualCards.actions'
import CreditCardsFullPaginatedDataReducer, {
  Actions as CreditCardsFullPaginatedDataActions,
  CreditCardsFullPaginatedDataState,
  initialState as initialCreditCardsFullPaginatedDataState,
} from 'src/redux/creditCardsFullPaginatedData.reducer'
import { createFlyoutReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface UpdateVirtualCardBaseState {
  receiptAttachmentIds: string[]
  uploadingAttachmentThumbnails: Record<string, string>
  puttingVirtualCard: boolean
  puttingVirtualCardCancel: boolean
  puttingVirtualCardReject: boolean
  puttingVirtualCardCancelUpdate: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  puttingVirtualCardFailure: boolean
}

export const initialBaseState: UpdateVirtualCardBaseState = {
  receiptAttachmentIds: [],
  uploadingAttachmentThumbnails: {},
  puttingVirtualCard: false,
  puttingVirtualCardCancel: false,
  puttingVirtualCardReject: false,
  puttingVirtualCardCancelUpdate: false,
  puttingVirtualCardFailure: false,
}

export type BaseActions =
  | UploadAttachmentActions
  | PutVirtualCardActions
  | PutVirtualCardCancelActions
  | SetReceiptAttachmentIdsAction
  | PutVirtualCardRejectActions
  | PutVirtualCardCancelUpdateActions
  | SetVirtualCardFailureAction

const BaseUpdateVirtualCardReducer = (
  state = initialBaseState,
  action: BaseActions,
): UpdateVirtualCardBaseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case SET_RECEIPT_ATTACHMENT_IDS: {
        draft.receiptAttachmentIds = action.payload.ids
        return draft
      }

      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.receiptAttachmentIds = [
          ...draft.receiptAttachmentIds,
          action.payload.id,
        ]
        return draft
      }

      case PUT_VIRTUAL_CARD_INIT: {
        draft.puttingVirtualCard = true
        draft.topLevelError = undefined
        draft.errorDetailMap = undefined
        return draft
      }

      case PUT_VIRTUAL_CARD_FAILURE: {
        draft.puttingVirtualCard = false
        if (isHandledPaywall402(action.payload)) {
          return draft
        }
        const errors = parseBackendErrors(action.payload.errorMessage)
        draft.topLevelError = errors.topLevelError
        draft.errorDetailMap = errors.errorDetailMap
        if (action.payload.status === 409) {
          draft.puttingVirtualCardFailure = true
        }
        return draft
      }

      case PUT_VIRTUAL_CARD_SUCCESS: {
        draft.puttingVirtualCard = false
        return draft
      }

      case PUT_VIRTUAL_CARD_CANCEL_INIT: {
        draft.puttingVirtualCardCancel = true
        return draft
      }

      case PUT_VIRTUAL_CARD_CANCEL_FAILURE: {
        draft.puttingVirtualCardCancel = false
        return draft
      }

      case PUT_VIRTUAL_CARD_CANCEL_SUCCESS: {
        draft.puttingVirtualCardCancel = false
        return draft
      }

      case SET_VIRTUAL_CARD_FAILURE: {
        draft.puttingVirtualCardFailure = false
        draft.topLevelError = undefined
        draft.errorDetailMap = undefined
        return draft
      }

      case PUT_VIRTUAL_CARD_REJECT_INIT: {
        draft.puttingVirtualCardReject = true
        return draft
      }

      case PUT_VIRTUAL_CARD_REJECT_SUCCESS: {
        draft.puttingVirtualCardReject = false
        return draft
      }

      case PUT_VIRTUAL_CARD_REJECT_FAILURE: {
        draft.puttingVirtualCardReject = false
        if (action.payload.status === 409) {
          draft.puttingVirtualCardFailure = true
        }
        return draft
      }

      case PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT: {
        draft.puttingVirtualCardCancelUpdate = true
        return draft
      }

      case PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS: {
        draft.puttingVirtualCardCancelUpdate = false
        return draft
      }

      case PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE: {
        draft.puttingVirtualCardCancelUpdate = false
        return draft
      }
    }
  })
}

export type UpdateVirtualCardState = UpdateVirtualCardBaseState & {
  creditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  recurringEnabledCreditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  guestAccessEnabledCreditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  balanceLockedEnabledCreditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
}

export const initialState: UpdateVirtualCardState = {
  ...initialBaseState,
  creditCardsFullPaginatedData: initialCreditCardsFullPaginatedDataState,
  recurringEnabledCreditCardsFullPaginatedData:
    initialCreditCardsFullPaginatedDataState,
  guestAccessEnabledCreditCardsFullPaginatedData:
    initialCreditCardsFullPaginatedDataState,
  balanceLockedEnabledCreditCardsFullPaginatedData:
    initialCreditCardsFullPaginatedDataState,
}

export type Actions = BaseActions | CreditCardsFullPaginatedDataActions

export const CoreUpdateVirtualCardReducer = (
  state = initialState,
  action: Actions,
): UpdateVirtualCardState => ({
  ...BaseUpdateVirtualCardReducer(state, action as BaseActions),
  creditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    (action.payload.queryParameters.recurrenceEnabled ||
      action.payload.queryParameters.directEnabled ||
      action.payload.queryParameters.balanceLockedEnabled)
      ? state.creditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.creditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
  recurringEnabledCreditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    !action.payload.queryParameters.recurrenceEnabled
      ? state.recurringEnabledCreditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.recurringEnabledCreditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
  guestAccessEnabledCreditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    !action.payload.queryParameters.directEnabled
      ? state.guestAccessEnabledCreditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.guestAccessEnabledCreditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
  balanceLockedEnabledCreditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    !action.payload.queryParameters.balanceLockedEnabled
      ? state.balanceLockedEnabledCreditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.balanceLockedEnabledCreditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
})

const UpdateVirtualCardFormReducer = createFlyoutReducer(
  [updateVirtualCardRouteProps, updateBillPayRouteProps],
  initialState,
  CoreUpdateVirtualCardReducer,
)

export default UpdateVirtualCardFormReducer
